import axios from 'axios';
import {
  AdoptionMetrics,
  EngagementMetrics,
  HealthMetrics,
  BehavioralMetrics,
  FunnelData,
  CohortData,
  DashboardOverviewResponse,
  CompanyInfo,
  TimeSeriesDataPoint,
  TransferFrequencyData,
  KpiQueryParams,
  MonthlyCompanyMetrics,
  ChurnAnalysisResponse,
  MonthlyCompanyChurnResponse,
  MonthlyCompanyHealthScore,
  WorkforceAnalysisResponse,
} from '../types/kpi.types';

const API_BASE_URL =
  process.env.REACT_APP_API_URL || 'http://localhost:3001/api';

const api = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
});

// ─── In-memory cache for GET requests (5 min TTL) ────────────────────────────
const _cache = new Map<string, { data: unknown; ts: number }>();
const CACHE_TTL = 5 * 60 * 1000;

const _originalGet = api.get.bind(api);
// @ts-ignore
api.get = async (url: string, config?: Parameters<typeof api.get>[1]) => {
  const key = url;
  const hit = _cache.get(key);
  if (hit && Date.now() - hit.ts < CACHE_TTL) {
    return { data: hit.data };
  }
  const response = await _originalGet(url, config);
  _cache.set(key, { data: response.data, ts: Date.now() });
  return response;
};

export const clearAnalyticsCache = () => _cache.clear();

export class AnalyticsService {
  private buildQueryString(params: KpiQueryParams): string {
    const queryParams = new URLSearchParams();
    if (params.companyId) queryParams.append('companyId', params.companyId);
    if (params.startDate) queryParams.append('startDate', params.startDate);
    if (params.endDate) queryParams.append('endDate', params.endDate);
    if (params.database) queryParams.append('database', params.database);
    return queryParams.toString();
  }

  async getAdoptionMetrics(
    params: KpiQueryParams = {},
  ): Promise<AdoptionMetrics> {
    const queryString = this.buildQueryString(params);
    const response = await api.get(`/analytics/kpis/adoption?${queryString}`);
    return response.data;
  }

  async getEngagementMetrics(
    params: KpiQueryParams = {},
  ): Promise<EngagementMetrics> {
    const queryString = this.buildQueryString(params);
    const response = await api.get(`/analytics/kpis/engagement?${queryString}`);
    return response.data;
  }

  async getMonthlyCompanyEngagement(
    params: KpiQueryParams = {},
  ): Promise<Array<import('../types/kpi.types').MonthlyCompanyEngagement>> {
    const queryString = this.buildQueryString(params);
    const response = await api.get(
      `/analytics/kpis/engagement/monthly-company?${queryString}`,
    );
    return response.data;
  }

  async getMonthlyAggregatedTrends(
    params: KpiQueryParams = {},
  ): Promise<Array<{ month: string; avgTransfersPerUser: number; totalVelocity: number }>> {
    const queryString = this.buildQueryString(params);
    const response = await api.get(
      `/analytics/kpis/engagement/monthly-aggregated-trends?${queryString}`,
    );
    return response.data;
  }

  async getHealthMetrics(params: KpiQueryParams = {}): Promise<HealthMetrics> {
    const queryString = this.buildQueryString(params);
    const response = await api.get(`/analytics/kpis/health?${queryString}`);
    return response.data;
  }

  async getBehavioralMetrics(
    params: KpiQueryParams = {},
  ): Promise<BehavioralMetrics> {
    const queryString = this.buildQueryString(params);
    const response = await api.get(
      `/analytics/kpis/behavioral?${queryString}`,
    );
    return response.data;
  }

  async getMonthlyCompanyTransferPatterns(
    params: KpiQueryParams = {},
  ): Promise<Array<import('../types/kpi.types').MonthlyCompanyTransferPatterns>> {
    const queryString = this.buildQueryString(params);
    const response = await api.get(
      `/analytics/kpis/transfer-patterns/monthly-company?${queryString}`,
    );
    return response.data;
  }

  async getTransfersByDayOfMonth(
    params: KpiQueryParams = {},
  ): Promise<Array<{ day: number; avgTransfers: number }>> {
    const queryString = this.buildQueryString(params);
    const response = await api.get(
      `/analytics/kpis/transfer-patterns/by-day-of-month?${queryString}`,
    );
    return response.data;
  }

  async getDaysToFirstTransferDistribution(
    params: KpiQueryParams = {},
  ): Promise<Array<{ daysRange: string; userCount: number }>> {
    const queryString = this.buildQueryString(params);
    const response = await api.get(
      `/analytics/kpis/behavioral/days-to-first-transfer-distribution?${queryString}`,
    );
    return response.data;
  }

  async getDaysBetweenTransfersDistribution(
    params: KpiQueryParams = {},
  ): Promise<Array<{ days: number; count: number }>> {
    const queryString = this.buildQueryString(params);
    const response = await api.get(
      `/analytics/kpis/behavioral/days-between-transfers-distribution?${queryString}`,
    );
    return response.data;
  }

  async getDaysBetweenTransfersExamples(
    params: KpiQueryParams & { limit?: number } = {},
  ): Promise<Array<{
    userId: number;
    transferDates: string[];
    gaps: number[];
    totalTransfers: number;
  }>> {
    const queryString = this.buildQueryString(params);
    const response = await api.get(
      `/analytics/kpis/behavioral/days-between-transfers-examples?${queryString}`,
    );
    return response.data;
  }

  async getNeverTransferProbability(
    params: KpiQueryParams = {},
  ): Promise<Array<{
    daysAfterRegistration: number;
    usersNotTransferredByDayX: number;
    usersNeverTransferred: number;
    usersEventuallyTransferred: number;
    neverTransferProbability: number;
  }>> {
    const queryString = this.buildQueryString(params);
    const response = await api.get(
      `/analytics/kpis/behavioral/never-transfer-probability?${queryString}`,
    );
    return response.data;
  }

  async getNeverSecondTransferProbability(
    params: KpiQueryParams = {},
  ): Promise<Array<{
    daysAfterFirstTransfer: number;
    usersNotMadeSecondByDayX: number;
    usersNeverMadeSecond: number;
    usersEventuallyMadeSecond: number;
    neverSecondTransferProbability: number;
  }>> {
    const queryString = this.buildQueryString(params);
    const response = await api.get(
      `/analytics/kpis/behavioral/never-second-transfer-probability?${queryString}`,
    );
    return response.data;
  }

  async getNeverThirdTransferProbability(
    params: KpiQueryParams = {},
  ): Promise<Array<{
    daysAfterSecondTransfer: number;
    usersNotMadeThirdByDayX: number;
    usersNeverMadeThird: number;
    usersEventuallyMadeThird: number;
    neverThirdTransferProbability: number;
  }>> {
    const queryString = this.buildQueryString(params);
    const response = await api.get(
      `/analytics/kpis/behavioral/never-third-transfer-probability?${queryString}`,
    );
    return response.data;
  }

  async getRegistrationDayOfMonthAnalysis(
    params: KpiQueryParams = {},
  ): Promise<Array<{
    dayOfMonth: number;
    totalRegistrations: number;
    medianDaysToFirstTransfer: number | null;
    usersWhoNeverTransferred: number;
    usersWhoNeverTransferredPercentage: number;
    usersWhoEventuallyTransferred: number;
  }>> {
    const queryString = this.buildQueryString(params);
    const response = await api.get(
      `/analytics/kpis/behavioral/registration-day-of-month-analysis?${queryString}`,
    );
    return response.data;
  }

  async getChurnRateByTransferCount(
    params: KpiQueryParams = {},
  ): Promise<Array<{
    transferCount: number;
    transferCountLabel: string;
    totalUsers: number;
    churnedUsers: number;
    churnPercentage: number;
  }>> {
    const queryString = this.buildQueryString(params);
    const response = await api.get(
      `/analytics/kpis/behavioral/churn-rate-by-transfer-count?${queryString}`,
    );
    return response.data;
  }

  async getDaysToFirstTransferEngagementAnalysis(
    params: KpiQueryParams = {},
  ): Promise<Array<{
    daysToFirstTransfer: number;
    daysLabel: string;
    userCount: number;
    avgTransfersAt30Days: number;
    avgTransfersAt60Days: number;
    avgTransfersAt90Days: number;
    avgTransfersPerMonth: number;
    medianTransfersAt30Days: number;
    medianTransfersAt60Days: number;
    medianTransfersAt90Days: number;
    usersWithSecondTransfer30d: number;
    usersWithSecondTransfer31to60d: number;
    usersWithSecondTransfer61to90d: number;
    usersWithOnlyOneTransfer: number;
    secondTransferRate30d: number;
    secondTransferRate31to60d: number;
    secondTransferRate61to90d: number;
    onlyOneTransferRate: number;
    medianDaysToSecondTransfer: number | null;
    usersWithShiftsLast60d: number;
    churnedUsersWithShifts: number;
    churnedPercentage: number;
  }>> {
    const queryString = this.buildQueryString(params);
    const response = await api.get(
      `/analytics/kpis/behavioral/days-to-first-transfer-analysis?${queryString}`,
    );
    return response.data;
  }

  async getDashboardOverview(
    params: KpiQueryParams = {},
  ): Promise<DashboardOverviewResponse> {
    const queryString = this.buildQueryString(params);
    const url = `/analytics/dashboard/overview?${queryString}`;
    console.log('=== API Call: getDashboardOverview ===');
    console.log('URL:', url);
    console.log('Params:', JSON.stringify(params));
    const response = await api.get(url);
    console.log('Response totalTransfers:', response.data.totalTransfers);
    return response.data;
  }

  async getAdoptionFunnel(params: KpiQueryParams = {}): Promise<FunnelData> {
    const queryString = this.buildQueryString(params);
    const response = await api.get(
      `/analytics/dashboard/funnel?${queryString}`,
    );
    return response.data;
  }

  async getMonthlyCompanyAdoption(
    params: KpiQueryParams = {},
  ): Promise<Array<import('../types/kpi.types').MonthlyCompanyAdoption>> {
    const queryString = this.buildQueryString(params);
    const response = await api.get(
      `/analytics/kpis/adoption/monthly-company?${queryString}`,
    );
    return response.data;
  }

  async getCohortData(params: KpiQueryParams = {}): Promise<CohortData> {
    const queryString = this.buildQueryString(params);
    const response = await api.get(
      `/analytics/dashboard/cohorts?${queryString}`,
    );
    return response.data;
  }

  async getMauTrend(
    params: KpiQueryParams = {},
  ): Promise<TimeSeriesDataPoint[]> {
    const queryString = this.buildQueryString(params);
    const response = await api.get(
      `/analytics/dashboard/mau-trend?${queryString}`,
    );
    return response.data;
  }

  async getHealthTrend(
    params: KpiQueryParams = {},
  ): Promise<TimeSeriesDataPoint[]> {
    const queryString = this.buildQueryString(params);
    const response = await api.get(
      `/analytics/dashboard/health-trend?${queryString}`,
    );
    return response.data;
  }

  async getTransferFrequencyDistribution(
    params: KpiQueryParams = {},
  ): Promise<TransferFrequencyData[]> {
    const queryString = this.buildQueryString(params);
    const response = await api.get(
      `/analytics/dashboard/transfer-frequency?${queryString}`,
    );
    return response.data;
  }

  async getAverageAmountByTransferNumber(
    params: KpiQueryParams = {},
  ): Promise<Array<{ transferNumber: number; averageAmount: number; userCount: number }>> {
    const queryString = this.buildQueryString(params);
    const response = await api.get(
      `/analytics/kpis/engagement/average-amount-by-transfer-number?${queryString}`,
    );
    return response.data;
  }

  async getCompanies(database?: string): Promise<CompanyInfo[]> {
    const queryString = database ? `?database=${database}` : '';
    const response = await api.get(`/analytics/companies/list${queryString}`);
    return response.data;
  }

  async getAvailableDatabases(): Promise<string[]> {
    const response = await api.get('/analytics/databases/list');
    return response.data;
  }

  async getPayroLogo(database?: string): Promise<string | null> {
    const queryString = database ? `?database=${database}` : '';
    const response = await api.get(`/analytics/branding/logo${queryString}`);
    return response.data?.logo || null;
  }

  async getMonthlyCompanyMetrics(params: KpiQueryParams = {}): Promise<MonthlyCompanyMetrics> {
    const queryString = this.buildQueryString(params);
    const response = await api.get(`/analytics/dashboard/monthly-breakdown?${queryString}`);
    return response.data;
  }

  async getChurnAnalysis(params: KpiQueryParams = {}): Promise<ChurnAnalysisResponse> {
    const queryParams = new URLSearchParams();
    if (params.companyId) queryParams.append('companyId', params.companyId);
    if (params.database) queryParams.append('database', params.database);
    const queryString = queryParams.toString();
    const response = await api.get(`/analytics/employees/churn?${queryString}`);
    return response.data;
  }

  async getMonthlyCompanyChurn(params: KpiQueryParams = {}): Promise<MonthlyCompanyChurnResponse> {
    const queryParams = new URLSearchParams();
    if (params.companyId) queryParams.append('companyId', params.companyId);
    if (params.database) queryParams.append('database', params.database);
    const queryString = queryParams.toString();
    const response = await api.get(`/analytics/employees/churn/monthly-company?${queryString}`);
    return response.data;
  }

  async getMonthlyCompanyHealthScores(params: KpiQueryParams = {}): Promise<MonthlyCompanyHealthScore[]> {
    const queryString = this.buildQueryString(params);
    const response = await api.get(`/analytics/dashboard/monthly-health-scores?${queryString}`);
    return response.data;
  }

  async getUserTransferAnalysis(params: KpiQueryParams = {}): Promise<Array<{
    userId: number;
    companyId: number;
    companyName: string;
    totalTransfers: number;
    totalAmount: number;
    avgTransferAmount: number;
    firstTransferDate: string;
    lastTransferDate: string;
    avgDaysBetweenTransfers: number;
    medianDaysBetweenTransfers: number;
    avgBalanceBeforeSubsequentTransfers: number;
  }>> {
    const queryString = this.buildQueryString(params);
    const response = await api.get(`/analytics/users/transfer-analysis?${queryString}`);
    return response.data;
  }

  async getMonthlyCompanyBehavioralMetrics(params: KpiQueryParams = {}): Promise<Array<{
    companyId: number;
    companyName: string;
    medianDaysToFirstTransfer: number;
    avgBalanceBeforeFirstTransfer: number;
  }>> {
    const queryParams = new URLSearchParams();
    if (params.startDate) queryParams.append('startDate', params.startDate);
    if (params.endDate) queryParams.append('endDate', params.endDate);
    if (params.database) queryParams.append('database', params.database);
    const queryString = queryParams.toString();
    const response = await api.get(`/analytics/kpis/behavioral/monthly-company-metrics?${queryString}`);
    return response.data;
  }

  async getMainMonthlyAvailableMonths(params: KpiQueryParams = {}): Promise<string[]> {
    const queryParams = new URLSearchParams();
    if (params.companyId) queryParams.append('companyId', params.companyId);
    if (params.database) queryParams.append('database', params.database);
    const queryString = queryParams.toString();
    const response = await api.get(`/analytics/main-monthly/available-months?${queryString}`);
    return response.data;
  }

  async getMainMonthlyMetrics(params: KpiQueryParams & { month: string }): Promise<{
    metrics: {
      transferAmountToday: number | null;
      transferAmountMonthly: number;
      transferAmountLastMonth: number | null;
      avgAmountPerTransferMonthly: number;
      avgAmountPerTransferLastMonth: number | null;
      avgAmountPerTransferMonthlyVsLastMonth: number | null;
      historicalAvgAmountPerTransfer: number;
      uniqueUsersTransferred: number;
      uniqueUsersTransferredLastMonth: number | null;
      firstTimeTransferrers: number;
      firstTimeTransferrersPercentage: number;
      totalTransfersMonthly: number;
      totalTransfersLastMonth: number | null;
      totalUsers: number;
      totalUsersLastMonth: number | null;
      totalAppEntrances: number;
      totalAppEntrancesLastMonth: number | null;
    };
    dailyAmounts: Array<{ day: number; date: string; amount: number }>;
    transferReasons: Array<{ reason: string; amount: number; percentage: number }>;
  }> {
    const queryParams = new URLSearchParams();
    queryParams.append('month', params.month);
    if (params.companyId) queryParams.append('companyId', params.companyId);
    if (params.database) queryParams.append('database', params.database);
    const queryString = queryParams.toString();
    const response = await api.get(`/analytics/main-monthly/metrics?${queryString}`);
    return response.data;
  }

  async getCompanyTransferTotals(database?: string): Promise<Array<{
    companyId: number;
    companyName: string;
    totalTransfers: number;
    totalAmount: number;
  }>> {
    const queryString = database ? `?database=${database}` : '';
    const response = await api.get(`/analytics/companies/transfer-totals${queryString}`);
    return response.data;
  }

  async getUserStatusByCompany(database?: string): Promise<Array<{
    companyId: number;
    companyName: string;
    active: number;
    notActive: number;
    blocked: number;
    left: number;
    total: number;
  }>> {
    const queryString = database ? `?database=${database}` : '';
    const response = await api.get(`/analytics/companies/user-status${queryString}`);
    return response.data;
  }

  async getUserSegments(params: KpiQueryParams = {}): Promise<Array<{
    segment: string;
    daysRange: string;
    userCount: number;
    avgAmountTransferred: number;
    avgTransfersPerUser: number;
    medianDaysBetweenTransfers: number;
    avgDaysBetweenTransfers: number;
    percentageSingleTransfer: number;
    percentageRetained: number;
  }>> {
    const queryString = this.buildQueryString(params);
    const response = await api.get(`/analytics/kpis/user-segments?${queryString}`);
    return response.data;
  }

  async getActiveUsersKpi(
    params: KpiQueryParams = {},
  ): Promise<import('../types/kpi.types').ActiveUsersKpiResponse> {
    const queryString = this.buildQueryString(params);
    const response = await api.get(`/analytics/active-users-kpi?${queryString}`);
    return response.data;
  }

  async getWorkforceAnalysis(params: KpiQueryParams): Promise<WorkforceAnalysisResponse> {
    const queryString = this.buildQueryString(params);
    const response = await api.get(`/analytics/workforce?${queryString}`);
    return response.data;
  }
}

export const analyticsService = new AnalyticsService();
